import React, { useMemo, useState } from "react";

import ShipeeziGrid, { Column, GeneralActions, RowActionsExtra } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { BsCheck as CheckIcon, BsX as CloseIcon } from "react-icons/bs";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { HiOutlinePlusCircle } from "react-icons/hi";

import { MarkNumbers } from "../../../../models/markNumbers";
import { Searches } from "../../../../models/shipeezi.table";
import { ViewOnly } from "../../../../types";
import { paginate } from "../../../../util/paginate";
import { Button } from "../../../Form";
import { Color } from "../../../Form/Button";
import { DefaultTooltip } from "../../../Generic/Tooltip";
import { EditableFieldTable } from "./EditableFieldTable";
import useStyles from "./styles";
import {
  ControlEditRow,
  EditMode,
  filterMarkAndNumbers,
  limitedCharactersMessage,
  SearchAttribute,
} from "./types";

const EmptyDocuments = require("../../../../assets/img/empty-documents.png");

interface MarkAndNumbersProps {
  openedFromView?: boolean;
  marks: MarkNumbers[];
  onAddMarkNumbers?: (marks: MarkNumbers[]) => void;
  onRemoveMarkNumbers?: (mark: MarkNumbers) => void;
  viewOnly: ViewOnly;
  loading?: boolean;
}

type TableRow = MarkNumbers & { editMode: EditMode };

const MarkAndNumbers: React.FC<MarkAndNumbersProps> = (props) => {
  const { marks, viewOnly: viewMode, onAddMarkNumbers, loading, openedFromView } = props;
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { fields: viewOnly } = viewMode;
  const [searchAttributes, setSearchAttributes] = useState<SearchAttribute[]>([]);
  const LINES_LIMIT = 99;

  const marksSorted = marks.sort((a, b) => {
    if (a.line < b.line) {
      return -1;
    }
    if (a.line > b.line) {
      return 1;
    }

    return 0;
  });
  const tableLength = marksSorted.length;

  const controlEditRows = useMemo(() => {
    const control = {} as ControlEditRow;

    for (let i = 1; i <= tableLength; i += +1) {
      control[i] = {
        editMode: EditMode.VIEW,
      };
    }

    return control;
  }, []);

  const [editedValues, setEditedValues] = useState<MarkNumbers[]>(marksSorted);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(6);
  const [rowsEditMode, setRowsEditMode] = useState<ControlEditRow>(controlEditRows);

  const markNumbersData = useMemo(() => {
    let data = marksSorted;

    if (searchAttributes.length > 0) {
      data = filterMarkAndNumbers(marksSorted, searchAttributes);
    }

    return paginate(data, page > 0 ? page - 1 : 0, pageSize);
  }, [marksSorted, page, pageSize, searchAttributes]);

  const handleChangeRowValue = (line: number, newValue: string, parameter: keyof MarkNumbers) => {
    setEditedValues((prevState) => {
      const currentMark = prevState.find((mark) => mark.line === line);
      const filteredMarks = prevState.filter((mark) => mark.line !== line);
      const newMark = currentMark?.line
        ? { ...currentMark, [parameter]: newValue }
        : { line, [parameter]: newValue };
      const newMarks = [...filteredMarks, newMark];

      return newMarks;
    });
  };

  const columns: Column[] = [
    {
      name: "line",
      title: `${translate("line")}#`,
      hideSort: true,
      className: classes.lineColumn,
      formatComponent: (value?: string) => <div className={classes.tableCell}>{value}</div>,
    },
    {
      name: "mark",
      title: `${translate("markAndNumbers")} (${limitedCharactersMessage(12, translate)})`,
      formatComponent: (value?: string, row?: MarkNumbers) => (
        <EditableFieldTable
          className={classes.markAndNumbersField}
          row={row as TableRow}
          rowsEditMode={rowsEditMode}
          editedValues={editedValues}
          defaultValue={value}
          parameter="mark"
          handleChangeRowValue={handleChangeRowValue}
          placeholder={limitedCharactersMessage(12, translate)}
          placeholderView={!viewOnly ? translate("empty") : ""}
          maxLength={12}
        />
      ),
      hideSort: true,
    },
    {
      name: "description",
      title: `${translate("description")} (${limitedCharactersMessage(30, translate)})`,
      formatComponent: (value?: string, row?: MarkNumbers) => (
        <EditableFieldTable
          className={classes.descriptionField}
          row={row as TableRow}
          rowsEditMode={rowsEditMode}
          editedValues={editedValues}
          defaultValue={value}
          parameter="description"
          handleChangeRowValue={handleChangeRowValue}
          placeholder={limitedCharactersMessage(30, translate)}
          placeholderView={!viewOnly ? translate("writeHere") : ""}
          maxLength={30}
        />
      ),
      hideSort: true,
    },
  ];

  interface EditButtonProps {
    rowId: number;
    row: TableRow;
  }
  const handleEditMode = (line: number, editMode: EditMode) => {
    setRowsEditMode((rows) => {
      return { ...rows, [line]: { editMode } };
    });
  };

  const EditButton = ({ row }: EditButtonProps) => {
    if (viewOnly) return null;
    const onConfirm = () => {
      const newMark = editedValues.find((mark) => mark.line === row.line);

      if (newMark) {
        handleEditMode(row.line, EditMode.EDITING);
        const filteredMarks = marksSorted.filter((mark) => mark.line !== row.line) ?? [];
        const newMarks = [...filteredMarks, newMark];

        onAddMarkNumbers?.(newMarks);
        handleEditMode(row.line, EditMode.VIEW);
      } else {
        handleEditMode(row.line, EditMode.VIEW);
      }
    };

    const onCancel = () => {
      handleEditMode(row.line, EditMode.CANCEL);
    };

    if (rowsEditMode?.[row.line]?.editMode === EditMode.EDITING) {
      const iconSize = 29;

      return (
        <div className={classes.editActionsContainer}>
          <DefaultTooltip title={translate("saveEdit")}>
            <div data-cy="action-confirm" onClick={() => onConfirm()}>
              <CheckIcon className={classes.button} color="#12B76A" size={iconSize} />
            </div>
          </DefaultTooltip>
          <DefaultTooltip title={translate("cancelEdit")}>
            <div data-cy="action-cancel" onClick={() => onCancel()}>
              <CloseIcon className={classes.button} color="#F04438" size={iconSize} />
            </div>
          </DefaultTooltip>
        </div>
      );
    }

    return (
      <DefaultTooltip title={translate("edit")}>
        <div
          data-cy="action-edit"
          className={classes.button}
          onClick={() => handleEditMode(row.line, EditMode.EDITING)}
        >
          <FiEdit color="#475467" size={18} />
        </div>
      </DefaultTooltip>
    );
  };

  const createNewLine = () => {
    const blankLine: MarkNumbers = {
      line: marksSorted.length + 1,
      mark: "",
      description: "",
    };

    if (viewOnly) return null;
    const disabledButton: boolean = markNumbersData?.length >= 99;
    const onCreateNewLine = () => {
      onAddMarkNumbers?.([...marksSorted, blankLine]);
      setEditedValues((prevState) => {
        return [...prevState, blankLine];
      });
    };

    return (
      <DefaultTooltip
        title={
          disabledButton
            ? `${translate("theLimitOfLinesIs")} ${LINES_LIMIT}`
            : translate("createNewRow")
        }
      >
        <div>
          <Button
            customColor={Color.PRIMARY}
            onClick={() => onCreateNewLine()}
            disabled={disabledButton}
          >
            <div className={classes.addMark}>
              <HiOutlinePlusCircle size={18} color="#FFF" />
              <span>{translate("createNewRow")}</span>
            </div>
          </Button>
        </div>
      </DefaultTooltip>
    );
  };

  const handleSearch = (searches: Searches) => {
    const attributes: SearchAttribute[] = [];

    if (searches) {
      searches.forEach((search) => {
        Object.getOwnPropertyNames(search).forEach((attribute) => {
          attributes.push({
            attribute,
            values: [search[attribute]],
          });
        });
      });
    }

    setSearchAttributes(attributes);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      // left: !openedFromView ? ["handleSearch"] : undefined,
      left: ["handleSearch"],
      right: ["createNewLine"],
    },
    createNewLine: createNewLine(),
    handleSearch: {
      onSearch: handleSearch,
    },
  };

  const handleDeleteButton = (line: number) => {
    const isEditMode = rowsEditMode?.[line]?.editMode === EditMode.EDITING;

    if (line !== tableLength || isEditMode || viewOnly) return null;

    return (
      <DefaultTooltip data-cy="action-delete" title={translate("delete")} placement="top">
        <div
          onClick={() => {
            const marksLineRemoved = marksSorted.slice(0, -1);

            handleChangeRowValue(line, "", "description");
            handleChangeRowValue(line, "", "mark");
            onAddMarkNumbers?.(marksLineRemoved);
          }}
        >
          <FiTrash2 color="#F97066" size={18} className={classes.button} />
        </div>
      </DefaultTooltip>
    );
  };

  const rowActionsExtra: RowActionsExtra[] = [
    {
      title: translate("edit"),
      buttonAndAction: (rowId, row: TableRow) => {
        return <EditButton rowId={rowId} row={row} />;
      },
    },
    {
      title: translate("delete"),
      buttonAndAction: (_, row) => handleDeleteButton(row.line),
    },
  ];

  const emptyData = () => (
    <div className={classes.emptyDataContainer}>
      <img src={EmptyDocuments} alt="empty-documents" width={450} height={334} />
      <span className={classes.emptyDataText}>{translate("noRowsCreatedYet")}</span>
    </div>
  );

  return (
    <div className={classes.mainDiv} data-cy="marks-container">
      <div className={classes.grid}>
        <ShipeeziGrid
          data={markNumbersData}
          totalItems={marks?.length}
          columns={columns}
          emptyData={emptyData}
          currentPage={page}
          onNextPage={setPage}
          onPreviousPage={setPage}
          onGoToPage={setPage}
          pageSize={pageSize}
          onChangePageSize={setPageSize}
          pageSizeList={[6, 10, 20, 99]}
          generalActions={generalActions}
          positionActionsColumn="right"
          rowActionsExtra={rowActionsExtra}
          showActionLabel={!viewOnly}
          showSelection={false}
          loadingData={loading}
          showColumnChooser={false}
        />
      </div>
    </div>
  );
};

export default MarkAndNumbers;
